/* HPH cart drawer fill + open (module). Replaces the old hph-cart-sync.js (June 18 rebuild).

   THE PROBLEM IT SOLVES
   The cart drawer is a STATIC body-level section (hph-cart-drawer). On add, Horizon's native
   cart-items-component tries to morph the drawer from the section HTML carried on the cart:update
   event, but for this static section that path does not reliably fill it, so the drawer opened
   empty. The previous fix (hph-cart-sync.js) filled it with raw `replaceChildren`, which swaps DOM
   nodes WITHOUT going through Horizon's hydrating morph, so the drawer that opened was not wired
   like a fresh server render ("the cart that opens is not the same as after refresh") and the
   buttons could be dead until reload.

   THE FIX
   On add only, call Horizon's OWN section renderer: sectionRenderer.renderSection() fetches the
   section via the Section Rendering API and morphs it in, which hydrates the controls exactly like
   a page load. Then we open the drawer OURSELVES, but only AFTER it is filled, so there is no empty
   flash and (since `auto-open` was removed) no reopen-on-delete.

   - Single writer on the drawer (Horizon's morph). No replaceChildren.
   - The header badge is a <cart-icon> that self-updates on this same event; we never touch it.
   - Delete / quantity changes come from inside the cart (source 'cart-items-component'); we ignore
     them so Horizon handles them natively and the drawer is not reopened.

   Loaded as type="module" in theme.liquid (needs the @theme importmap). */
import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'hph-cart-drawer';
let busy = false;

// The native cart-items-component may also attempt morphSection from the add.js section payload;
// for this static section that can reject with "not found in the section rendering response".
// It is benign (it makes no DOM change and we fill via renderSection below), so swallow ONLY that
// specific rejection to keep the console clean.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason && (reason.message || String(reason));
  if (message && /not found in the section rendering response/i.test(message)) {
    event.preventDefault();
  }
});

document.addEventListener('cart:update', async (event) => {
  const data = event.detail && event.detail.data;
  if (!data || data.source !== 'product-form-component') return; // adds only

  // Not present on the cart page (drawer is not rendered there).
  if (!document.getElementById('shopify-section-' + SECTION_ID)) return;

  if (busy) return;
  busy = true;
  try {
    // Horizon's renderer: fetch the section fresh + hydrating morph (matches a page load).
    await sectionRenderer.renderSection(SECTION_ID, { cache: false });
  } catch (error) {
    // ignore - we still open so the user sees the drawer
  } finally {
    busy = false;
  }

  // Open AFTER the fill so the empty state never flashes. Re-query: morph preserves the element by
  // id, but re-querying is safe regardless.
  const drawer = document.getElementById(SECTION_ID);
  if (drawer && typeof drawer.open === 'function') drawer.open();
});
